/**
 * Klaviyo Functions
 *
 * Two transports carry Klaviyo events:
 *
 * 1. klaviyo.js, Klaviyo's onsite script. It owns the visitor identity (the
 *    __kla_id cookie), renders Klaviyo forms and popups, and is the only way
 *    to feed the recently viewed store (trackViewedItem) and to identify a
 *    visitor. The Pixel Manager loads it itself in takeover and standalone
 *    mode, consent-gated, on every page except checkout. Klaviyo's own plugin
 *    skips checkout on purpose so no popup interrupts a buyer, and this
 *    matches that.
 *
 * 2. Klaviyo's public client endpoint, POST /client/events, which takes the
 *    public API key and needs no script. Every tracked event goes through it,
 *    because it is the only place the metric can be tagged with service
 *    "woocommerce", which puts the event on the same Klaviyo metric the
 *    WooCommerce integration uses instead of a generic API metric. It also
 *    works on the checkout page, where klaviyo.js is not loaded, and in
 *    gap-fill mode, where the Klaviyo plugin's own klaviyo.js is on the page
 *    and a second copy must never load.
 *
 * @see https://developers.klaviyo.com/en/docs/javascript_api
 * @see https://developers.klaviyo.com/en/reference/create_client_event
 */

(function (pmw, undefined) {

	const CLIENT_EVENTS_ENDPOINT = "https://a.klaviyo.com/client/events";
	const API_REVISION           = "2026-07-15";

	// Handles that identified the visitor to klaviyo.js during this page
	// load, so identify() runs once per email rather than once per event.
	const identifiedEmails = new Set();

	const settings = () => pmwDataLayer?.pixels?.klaviyo || {};

	/**
	 * Whether klaviyo.js is loaded by the Pixel Manager on this page.
	 *
	 * Never in gap-fill mode, where the Klaviyo plugin loads it, and never on
	 * the checkout page.
	 */
	pmw.klaviyoLoadsScript = () => {
		if ("gap_fill" === settings().mode) return false;
		if ("checkout" === pmwDataLayer?.shop?.page_type) return false;
		return true;
	};

	/**
	 * Load the Klaviyo pixel
	 *
	 * Installs Klaviyo's official klaviyo object stub, so identify() and
	 * trackViewedItem() calls made before the script arrives are queued, then
	 * injects klaviyo.js. In gap-fill mode and on checkout nothing is loaded,
	 * and events go through the client endpoint only.
	 */
	pmw.loadKlaviyoPixel = () => {
		try {
			const config = settings();

			if (pmw.klaviyoLoadsScript()) {

				// Klaviyo's official onsite snippet, unmodified.
				// @formatter:off
				/* eslint-disable */
				!function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();
				/* eslint-enable */
				// @formatter:on

				if (!document.querySelector("script[src*=\"static.klaviyo.com/onsite/js/\"]")) {
					const script = document.createElement("script");
					script.async = true;
					script.src   = "https://static.klaviyo.com/onsite/js/" + encodeURIComponent(config.public_api_key) + "/klaviyo.js";
					document.head.appendChild(script);
				}
			}

			pmwDataLayer.pixels.klaviyo.loaded = true;

			pmw.console.log("Klaviyo: pixel loaded", {
				mode  : config.mode,
				script: pmw.klaviyoLoadsScript() ? "klaviyo.js loaded by the Pixel Manager" : "events through the client endpoint only",
			});

			pmw.klaviyoIdentify();

			if ("takeover" === config.mode) {
				setTimeout(pmw.klaviyoTakeoverSelfCheck, 1500);
			}

		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * What the shopper has typed into the checkout so far, from the platform
	 * layer, which knows the checkout's fields. Read when an event fires rather
	 * than through change listeners on selectors, which is what breaks the
	 * Klaviyo plugin's Started Checkout on custom checkouts.
	 */
	const checkoutContact = () => {
		try {
			return pmw.platform?.getCheckoutContact?.() || {};
		} catch (e) {
			return {};
		}
	};

	/**
	 * The email address the Pixel Manager knows for this visitor, or null.
	 *
	 * The logged-in customer first, then the email typed into the checkout, and
	 * on the confirmation page the order's.
	 */
	pmw.klaviyoGetEmail = () => {
		try {
			const userEmail = pmwDataLayer?.user?.email?.raw;
			if (userEmail && pmw.isEmail(userEmail)) return userEmail;

			const orderEmail = pmwDataLayer?.order?.billing_email;
			if (orderEmail && pmw.isEmail(orderEmail)) return orderEmail;

			const typed = String(checkoutContact().email || "").trim();
			if (typed && pmw.isEmail(typed)) return typed;
		} catch (e) {
			// Nothing known on most pages.
		}

		return null;
	};

	/**
	 * The anonymous Klaviyo profile identifier from the __kla_id cookie, or null.
	 *
	 * klaviyo.js writes base64(JSON), the Klaviyo plugin's checkout script
	 * base64(encodeURIComponent(JSON)). Both decode here.
	 */
	pmw.klaviyoGetExchangeId = () => {
		try {
			const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]*)/);
			if (!match) return null;

			const raw = decodeURIComponent(match[1]);
			let json;

			try {
				json = atob(raw);
			} catch (e) {
				return null;
			}

			let decoded;

			try {
				decoded = JSON.parse(json);
			} catch (e) {
				decoded = JSON.parse(decodeURIComponent(json));
			}

			const exchangeId = decoded?.$exchange_id;

			return exchangeId && /^[A-Za-z0-9=_-]{8,255}$/.test(exchangeId) ? exchangeId : null;
		} catch (e) {
			return null;
		}
	};

	/**
	 * The profile an event is recorded against, or null when Klaviyo could not
	 * attach it to anyone.
	 *
	 * The email identifies the profile only when customer identification is
	 * enabled; otherwise the anonymous exchange ID from the cookie does, which
	 * is also how the Klaviyo plugin attributes its own events.
	 */
	pmw.klaviyoGetProfile = () => {
		const profile = {};

		if (settings().identify_customers) {
			const email = pmw.klaviyoGetEmail();
			if (email) profile.email = email;
		}

		const exchangeId = pmw.klaviyoGetExchangeId();
		if (exchangeId) profile._kx = exchangeId;

		return Object.keys(profile).length ? profile : null;
	};

	/**
	 * Identify the visitor to klaviyo.js
	 *
	 * Runs once per email per page load, and needs the klaviyo object, so it
	 * does nothing in gap-fill mode until the plugin's klaviyo.js is present,
	 * and nothing on checkout, where the client endpoint carries the email in
	 * the event profile instead.
	 */
	pmw.klaviyoIdentify = () => {
		try {
			if (!settings().identify_customers) return;

			const email = pmw.klaviyoGetEmail();
			if (!email || identifiedEmails.has(email)) return;

			if (typeof window.klaviyo?.push !== "function") return;

			const properties = {$email: email};
			const contact    = checkoutContact();

			const firstName = pmwDataLayer?.user?.first_name?.raw || contact.firstName;
			const lastName  = pmwDataLayer?.user?.last_name?.raw || contact.lastName;
			const phone     = pmwDataLayer?.user?.phone?.raw;

			if (firstName) properties.$first_name = firstName;
			if (lastName) properties.$last_name = lastName;
			if (phone) properties.$phone_number = phone;

			window.klaviyo.push([ "identify", properties ]);
			identifiedEmails.add(email);

			pmw.console.log("Klaviyo: visitor identified", {email: email});

		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Feed Klaviyo's recently viewed store
	 *
	 * Browse abandonment flows and the "recently viewed" product blocks read
	 * from it. Only klaviyo.js can write it.
	 */
	pmw.klaviyoTrackViewedItem = (item) => {
		try {
			if (!item || typeof window.klaviyo?.push !== "function") return;

			window.klaviyo.push([ "trackViewedItem", item ]);

		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Wait briefly for the __kla_id cookie
	 *
	 * On the first page view klaviyo.js has just been injected and has not set
	 * its cookie yet when the first events fire. A short wait lets the event
	 * attach to the visitor's profile instead of being dropped.
	 */
	const waitForProfile = () => new Promise(resolve => {
		let attempts = 0;

		const check = () => {
			const profile = pmw.klaviyoGetProfile();
			if (profile || attempts >= 15) return resolve(profile);
			attempts++;
			setTimeout(check, 200);
		};

		check();
	});

	/**
	 * Send an event to Klaviyo
	 *
	 * @param {string} eventName  - The Klaviyo metric name
	 * @param {Object} properties - Event properties
	 * @param {string} [uniqueId] - Deduplication ID; Klaviyo keeps the first event per profile, metric and unique_id
	 * @param {number} [value]    - The event value
	 */
	pmw.klaviyoTrack = async (eventName, properties, uniqueId, value) => {
		try {
			if (!eventName) return;

			const config = settings();
			if (!config.public_api_key) return;

			let profile = pmw.klaviyoGetProfile();

			if (!profile && pmw.klaviyoLoadsScript()) {
				profile = await waitForProfile();
			}

			if (!profile) {
				pmw.console.log("Klaviyo: " + eventName + " event not sent, the visitor is not identifiable yet", properties);
				return;
			}

			// The service tag names the shop platform's Klaviyo integration and
			// comes from the server, so the library stays platform-agnostic.
			const metricAttributes = {name: eventName};
			if (config.metric_service) metricAttributes.service = config.metric_service;

			const attributes = {
				properties: properties || {},
				metric    : {
					data: {
						type      : "metric",
						attributes: metricAttributes,
					},
				},
				profile   : {
					data: {
						type      : "profile",
						attributes: profile,
					},
				},
			};

			if (uniqueId) attributes.unique_id = String(uniqueId);

			if (typeof value === "number" && Number.isFinite(value)) {
				attributes.value = value;
				if (pmwDataLayer?.shop?.currency) attributes.value_currency = pmwDataLayer.shop.currency;
			}

			fetch(CLIENT_EVENTS_ENDPOINT + "?company_id=" + encodeURIComponent(config.public_api_key), {
				method   : "POST",
				keepalive: true,
				headers  : {
					"Content-Type": "application/vnd.api+json",
					"revision"    : API_REVISION,
				},
				body     : JSON.stringify({data: {type: "event", attributes: attributes}}),
			}).catch(e => console.error(e));

			pmw.console.log("Klaviyo: " + eventName + " event sent", properties);

		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Confirm the takeover of the Klaviyo plugin's tracking took effect
	 *
	 * Looks for the plugin's script tags, which WordPress prints with the
	 * handle as the id, and for a second copy of klaviyo.js. Both mean a
	 * Klaviyo plugin update moved a hook and the plugin's own tracking is
	 * running again alongside this one.
	 */
	pmw.klaviyoTakeoverSelfCheck = () => {
		try {
			const handles  = settings().takeover_handles || [];
			const leftover = handles.filter(handle => document.getElementById(handle + "-js"));
			const copies   = document.querySelectorAll("script[src*=\"static.klaviyo.com/onsite/js/\"]").length;

			if (!leftover.length && copies <= 1) {
				pmw.console.log("Klaviyo: takeover self-check passed, the Klaviyo plugin tracking scripts are gone");
				return;
			}

			pmw.console.log("Klaviyo: takeover self-check FAILED, the Klaviyo plugin is still tracking. Its events would be counted twice.", {
				leftover_scripts: leftover,
				klaviyo_js_tags : copies,
			});

		} catch (e) {
			console.error(e);
		}
	};

}(window.pmw = window.pmw || {}));
